import { useGLTF } from "@react-three/drei";
import gsap from "gsap/gsap-core";
import React, { useEffect, useRef, useState } from "react";
import getMeshFromModel, { colors } from "../getMeshFromModel";
import useStore from "../useStore";
import {
  harvestingSpaceman,
  lyingSpaceman,
  sitSpaceman,
  standSpaceman,
} from "./assets/spaceman";

const Face = ({ mouthOpen = false, eyesAreClosed = false, ...props }) => {
  const eyes = useRef();
  const [mouthIsOpen, setMouthIsOpen] = useState(mouthOpen);

  useEffect(() => {
    const unsubscribe = useStore.subscribe(
      (activeProject) => {
        if (activeProject !== null) {
          setMouthIsOpen(true);
        } else {
          setMouthIsOpen(false);
        }
      },
      (state) => state.activeProject
    );
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const eyesElement = eyes.current;
    if (eyesElement && !eyesAreClosed) {
      const interval = setInterval(() => {
        gsap.to(eyesElement.children[0].scale, {
          y: 0.1,
          repeat: 1,
          duration: 0.1,
          yoyo: true,
        });
        gsap.to(eyesElement.children[1].scale, {
          y: 0.1,
          repeat: 1,
          duration: 0.1,
          yoyo: true,
        });
      }, 5000);
      return () => {
        clearInterval(interval);
      };
    }
  }, [eyes, eyesAreClosed]);

  return (
    <group ref={eyes} {...props}>
      <mesh position={[3, 9, 4.1]} scale={[1, eyesAreClosed ? 0.1 : 1, 1]}>
        <boxBufferGeometry args={[1, 1, 1]} />
        <meshStandardMaterial attach="material" color={colors["02"]} />
      </mesh>
      <mesh position={[6, 9, 4.1]} scale={[1, eyesAreClosed ? 0.1 : 1, 1]}>
        <boxBufferGeometry args={[1, 1, 1]} />
        <meshStandardMaterial attach="material" color={colors["02"]} />
      </mesh>
      <mesh position={[4.5, 8, 4.1]} scale={mouthIsOpen ? 1 : 0}>
        <boxBufferGeometry args={[2, 1, 1]} />
        <meshStandardMaterial attach="material" color={colors["02"]} />
      </mesh>
    </group>
  );
};

const Glass = ({ ...props }) => {
  const { nodes } = useGLTF(process.env.PUBLIC_URL + "/models/glass.glb");
  return (
    <mesh
      castShadow
      receiveShadow
      geometry={nodes.Mesh_0003.geometry}
      scale={0.999}
      {...props}
    >
      <meshPhongMaterial color="#000" opacity={0.4} transparent={true} />
    </mesh>
  );
};

/** @param {{ posture: "stand" | "sit" | "harvesting" | "lying"; [x: string]: any }} props */
const Spaceman = ({ posture, ...props }) => {
  let meshModel = standSpaceman;
  let detailsPosition = [0, 0, 0];
  let eyesAreClosed = false;
  let mouthOpen = false;

  switch (posture) {
    case "stand":
      meshModel = standSpaceman;
      break;
    case "sit":
      meshModel = sitSpaceman;
      detailsPosition = [0, -1, 0];
      break;
    case "harvesting":
      meshModel = harvestingSpaceman;
      break;
    case "lying":
      meshModel = lyingSpaceman;
      eyesAreClosed = true;
      mouthOpen = true;
      break;
    default:
      break;
  }

  const propsMesh = getMeshFromModel(meshModel);

  return (
    <group {...props}>
      {propsMesh}
      <Glass position={detailsPosition} />
      <Face
        position={detailsPosition}
        mouthOpen={mouthOpen}
        eyesAreClosed={eyesAreClosed}
      />
    </group>
  );
};

export default Spaceman;
